import { useState } from 'react';
import { Link } from '@material-ui/core';
import { Flex, Padding } from 'components/base/layout-components';
import styled from 'styled-components';
import { getColor } from 'common/constants/_colors';
import Icon from 'components/base/Icon';
import Text from 'components/base/Text';
import { LINE_HEIGHT } from 'common/constants/sizes';
import { learnMoreReauthUrl } from 'common/constants/articles';
import { LBButton } from '@linearb/buzz';
import {
  SESSION_STORAGE_KEYS,
  TOKEN_VERSION_FOR_BB_GITSTREAM_INTEGRATION
} from 'common/constants/constants';
import { isCmRepo } from 'views/root/views/home/views/settings/views/RepositoriesV2/gitStreamIntegration/gitStream.service';
import BrowserStorageAccessor from 'common/utils/BrowserStorageAccessor';

const StyledFlex = styled(Flex)`
  border: 1px solid ${getColor('pastelPink')};
  background-color: ${getColor('lavenderBlush')};
  padding: 2rem;
`;

const ReposWarning = ({ names = [] }) => {
  const [one, two, three, ...rest] = names;
  return (
    <StyledFlex>
      <Flex pr='small'>
        <Icon name='MuiErrorOutline' color='torchRed' size='medium'></Icon>
      </Flex>
      <Flex grow='1' direction='column'>
        <Text size='large'>
          We will not have access to the following repositories:
        </Text>
        <Text size='large' weight='bold'>
          {[one, two, three]
            .filter(Boolean)
            .map(({ name }) => name)
            .join(', ')}
          {rest.length ? ` (+${rest.length})` : ''}
        </Text>
      </Flex>
    </StyledFlex>
  );
};

export const ScaledReposMessage = ({ sensor_name }) => {
  return (
    <>
      <Text
        size='large'
        lineHeight={LINE_HEIGHT.large}
        pb='1rem'
        data-testid='scaled-repos-message'
      >
        Note:
        <br />
        Please verify you are connecting with a {sensor_name} organization owner
        or admin permissions.
        <br />
        <br />
        Once reauthorized, LinearB will not have access to repositories of which
        you are not an owner or collaborator.
        <br />
        <br />
        Changes may take some time to take effect.
      </Text>
      <Text size='medium'>
        <Link onClick={() => window.open(learnMoreReauthUrl, '_blank')}>
          Learn more
        </Link>
      </Text>
    </>
  );
};

export const ReauthV1Message = ({
  unavailableRepositories = [],
  alreadySelectedRepositories = [],
  sensorAuthorizationRepositories = []
}) => {
  return (
    <>
      <Text
        size='large'
        lineHeight={LINE_HEIGHT.large}
        pb='1rem'
        data-testid='reauth-message'
      >
        Once you reauthorize the integration -<br />
        LinearB will have access to {alreadySelectedRepositories.length}/
        {sensorAuthorizationRepositories.length} repositories.
      </Text>
      {unavailableRepositories.length ? (
        <ReposWarning names={unavailableRepositories}></ReposWarning>
      ) : null}
      <Text size='medium'>
        <Link onClick={() => window.open(learnMoreReauthUrl, '_blank')}>
          Learn more
        </Link>
      </Text>
    </>
  );
};

export const ScaledReposButtons = ({
  sensor_authorization_id,
  reauthParams,
  onApproveReauthV2,
  toggleModal,
  toggleSnackBar,
  openRepositoriesModal,
  gitIntegrations = [],
  isGitstreamFlow,
  sensorAuthorizationRepositories = []
}) => {
  const [pendingReauth, setPendingReauth] = useState(false);
  const sensorAuthorization = gitIntegrations.find(
    ({ id }) => id === sensor_authorization_id
  );
  const isV3BBSensorAuthorization =
    sensorAuthorization?.profile?.token_version ===
    TOKEN_VERSION_FOR_BB_GITSTREAM_INTEGRATION;

  const cmRepo = sensorAuthorizationRepositories.find(
    repo =>
      repo.sensor_authorization_id === Number(sensor_authorization_id) &&
      isCmRepo(repo?.name)
  );

  // const hasActiveCmRepo = cmRepo?.extra?.is_gitstream_enabled ?? false;
  return (
    <Flex direction='row' padding='normal' pl='big' pr='big' justify='end'>
      <LBButton
        variant='outline'
        disabled={pendingReauth}
        onClick={() => toggleModal()}
      >
        Cancel
      </LBButton>
      <Flex pl='small'>
        <LBButton
          data-cy='git-reauth-approve'
          data-testid='git-reauth-approve'
          disabled={pendingReauth}
          onClick={async () => {
            try {
              setPendingReauth(true);
              await onApproveReauthV2({
                reauthParams,
                sensor_authorization_id,
                isGitstreamFlow
              });
              if ((isGitstreamFlow || isV3BBSensorAuthorization) && !cmRepo) {
                // open in a cm repo selector - using this flag isConnectGitStreamFlow for gitstream reauth
                openRepositoriesModal({
                  sensorAuth: sensorAuthorization,
                  isConnectGitStreamFlow: true
                });
              } else {
                // This is for the case where the user has a cm repo connected to gitstream and they are reauthorizing the integration
                // We need to change the button to disconnect
                if (isGitstreamFlow) {
                  BrowserStorageAccessor().session.set(
                    SESSION_STORAGE_KEYS.UPDATE_GITSTREAM_ON_INTEGRATION_ID,
                    sensor_authorization_id
                  );
                }
                toggleModal();
              }
            } catch (e) {
              toggleSnackBar({
                message: (
                  <Text size='large' color='white'>
                    Failed to reauthorize
                  </Text>
                ),
                variant: 'error'
              });
            }
          }}
        >
          Approve
        </LBButton>
      </Flex>
    </Flex>
  );
};

export const ReauthV1Buttons = ({
  gitIntegrations = [],
  openRepositoriesModal,
  sensor_authorization_id,
  reauthParams,
  onApproveReauth,
  toggleModal,
  toggleSnackBar,
  isGitstreamFlow = false
}) => {
  const [pendingReauth, setPendingReauth] = useState(false);
  const sensorAuthorization = gitIntegrations.find(
    ({ id }) => id === sensor_authorization_id
  );

  const handleGitReauthApproveAndAdd = async () => {
    setPendingReauth(true);
    try {
      await onApproveReauth({
        reauthParams,
        sensor_authorization_id,
        isGitstreamFlow
      });
      toggleModal();
      // open in a cm repo selector - using this flag isConnectGitStreamFlow for gitstream reauth
      openRepositoriesModal({
        sensorAuth: sensorAuthorization,
        isConnectGitStreamFlow: true
      });
    } catch (err) {
      toggleSnackBar({
        message: (
          <Text size='large' color='white'>
            Failed to reauthorize
          </Text>
        ),
        variant: 'error'
      });
    }
    setPendingReauth(false);
  };

  const handleGitReauthApprove = async () => {
    setPendingReauth(true);
    try {
      await onApproveReauth({
        reauthParams,
        sensor_authorization_id
      });
      toggleModal();
    } catch (err) {
      toggleSnackBar({
        message: (
          <Text size='large' color='white'>
            Failed to reauthorize
          </Text>
        ),
        variant: 'error'
      });
    }
    setPendingReauth(false);
  };

  return (
    <Flex
      direction='row'
      padding='normal'
      pl='big'
      pr='big'
      justify='space-between'
    >
      <LBButton
        variant='outline'
        disabled={pendingReauth}
        onClick={() => toggleModal()}
      >
        Cancel
      </LBButton>
      <Flex>
        <LBButton
          data-testid='git-reauth-approve-and-add'
          variant='outline'
          disabled={pendingReauth}
          onClick={handleGitReauthApproveAndAdd}
        >
          Approve and add repos
        </LBButton>
        <Padding r='small'></Padding>
        {!isGitstreamFlow && (
          <LBButton
            data-cy='git-reauth-approve'
            data-testid='git-reauth-approve'
            disabled={pendingReauth}
            onClick={handleGitReauthApprove}
          >
            Approve
          </LBButton>
        )}
      </Flex>
    </Flex>
  );
};
